import {
  type Country as ZoneCountry,
  zoneFromBrandAndCountry,
  type Zone,
} from '@seek/audience-zones';
import type { CompatibleLanguage } from '@seek/ca-myprofile-utils';
import type { Brand, Country } from '@seek/melways-sites';
import type { Dispatch } from 'redux';

import analytics from 'src/modules/analytics/accountMiddlewareAnalytics';
import { isGraphQLAuthError } from 'src/modules/graphql/errors';

import type { PageUpdateActionType } from '../page/actionCreators';

export enum UserActionType {
  SUCCESS = 'USER_SUCCESS',
  ERROR = 'USER_ERROR',
  UPDATE_USER_STATE = 'UPDATE_USER_STATE',
  USER_DELETED = 'USER_DELETED',
  SET_USER_DETAILS = 'SET_USER_DETAILS',
}

export interface UserAction {
  type: UserActionType | PageUpdateActionType;
  payload?: any;
}

export interface ChangeEmailModel {
  confirmEmail: string;
  currentPassword: string;
  newEmail: string;
  error?: string;
}

export interface ChangePasswordModel {
  confirmPassword: string;
  currentPassword: string;
  newPassword: string;
}

export type GetUser = (
  language: CompatibleLanguage,
  country: Country,
  brand: Brand,
) => (arg: { dispatch: Dispatch<UserAction>; api: any }) => void;

export const getUser: GetUser =
  (language: CompatibleLanguage = 'en', country: Country, brand: Brand) =>
  async ({ dispatch, api }) => {
    try {
      const {
        data: { viewer: data },
      } = await api.getUser();
      dispatch({
        type: UserActionType.SUCCESS,
        payload: data,
      });
      return data;
    } catch (error: any) {
      const userNotAuthenticated: boolean = isGraphQLAuthError(error);
      // send a fail event and then dispatch action that redirects
      const countryForAudienceZones = country?.toUpperCase() as ZoneCountry;
      const zone =
        zoneFromBrandAndCountry(brand as Brand, countryForAudienceZones) || '';
      const { trackLink } = analytics({
        pageAnalytics: {
          siteCountry: country,
          brand,
          siteLanguage: language,
          zone: zone as Zone,
        },
      });
      trackLink({
        eventName: 'settings_displayed',
        loginId: error?.id,
        isLoggedIn: false,
        profileGuid: error?.emailCorrelationId,
        error,
      });
      dispatch({
        type: UserActionType.ERROR,
        payload: {
          language,
          userNotAuthenticated,
        },
      });
    }
  };

export const updateUserInfo =
  (loginId?: string, isLoggedIn?: boolean, profileGuid?: string) =>
  ({ dispatch }: { dispatch: Dispatch<UserAction>; getState: any }) => {
    dispatch({
      type: UserActionType.SET_USER_DETAILS,
      payload: {
        loginId,
        isLoggedIn,
        profileGuid,
      },
    });
  };
